import {
  IocSelectorOption,
  IocSelectorService,
} from './../../../../_shared/components/ioc-selector/ioc-selector.service';
import { Injectable, inject } from '@angular/core';
import { Match, Side } from '../../../../_shared/models/match.model';
import {
  ScoreFormat,
  SetType,
} from '../../../../_shared/models/score-format.model';
import { BoardHelpService } from '../../board-help.service';
import {
  Point,
  Server,
  PlayerInfo,
  PlayerCourtPosition,
} from '../../../../_shared/models/point.model';
import { CourtSide } from '../../../../_shared/models/enums/court-side';
import { SquashGameType } from '../../../../_shared/models/enums/squash.enum';

type PlayersMap = Record<number, Array<{
  playerName: string;
  sideNumber: number;
  playerNumber: number;
  player: string | null;
}>>;

@Injectable({
  providedIn: 'root',
})
export class SquashTrackerBrainService {
  private iocSelectorService = inject(IocSelectorService);
  private bhs = inject(BoardHelpService);

  private side1PlayerPosition: PlayerCourtPosition = {};
  private side2PlayerPosition: PlayerCourtPosition = {};

  constructor() {}

  /* --------------------------- Public API --------------------------- */

  async selectServer(
    match: Match,
    showCancelButton: boolean,
    serverNumber?: number,
    sideNumber?: number
  ): Promise<Server | undefined> {
    const players = this.buildPlayersMap(match);
    const options = this.buildServerOptions(players, serverNumber, sideNumber);

    const selectedServer = await this.iocSelectorService.openPopover(
      'Select server',
      options,
      showCancelButton
    );

    if (!selectedServer) return undefined;

    const returnerPlayerNumber =
      match.matchType === 'D'
        ? await this.selectReturner(match, selectedServer.returnerSideNumber, showCancelButton)
        : 1;

    if (!returnerPlayerNumber) return undefined;

    selectedServer.returnerPlayerNumber = returnerPlayerNumber;
    const returnerSide = selectedServer.returnerSideNumber || 2;
    selectedServer.returnerPlayerId =
      match?.sides?.[returnerSide - 1]?.players?.[returnerPlayerNumber - 1]?.participant?._id ?? null;
    selectedServer.returningSide = CourtSide.DEUCE;

    this.assignPlayerPositions(players, selectedServer);

    return selectedServer;
  }

  public getPlayerPosition(side: number): PlayerCourtPosition | null {
    return (this as any)[`side${side}PlayerPosition`] ?? null;
  }

  changeServer(
    match: Match,
    scoreFormat: ScoreFormat,
    pointWinnerSideNumber: number,
    isNewSet?: boolean
  ): { server?: Server; suggestServerChange: boolean } {
    if (match.matchType === 'S') {
      return this.changeServerForSingles(match, pointWinnerSideNumber, isNewSet);
    }
    return this.changeServerForDoubles(match, pointWinnerSideNumber, isNewSet);
  }

  clearTieBreakChoice(match: Match): void {
    const lastGame = this.bhs.getLastGame(match);
    if (!lastGame?.extensions) return;

    const idx = lastGame.extensions.findIndex((e) => e.name === 'squashTieBreakChoice');
    if (idx >= 0) lastGame.extensions.splice(idx, 1);
  }

  shouldShowTieBreakModal(
    match: Match,
    scoreFormat: ScoreFormat,
    side1Score: number,
    side2Score: number
  ): boolean {
    const gameType = this.getGameType(match);
    if (gameType !== SquashGameType.ENGLISH_HIHO) return false;

    const numberOfPointsInGame = scoreFormat.numberOfPointsInGame || 11;
    const lastGame = this.bhs.getLastGame(match);

    const isAtTieBreakPoint =
      (numberOfPointsInGame === 9 && side1Score === 8 && side2Score === 8) ||
      (numberOfPointsInGame === 15 && side1Score === 14 && side2Score === 14);

    if (lastGame?.extensions?.find((ext) => ext.name === 'squashTieBreakChoice') && !isAtTieBreakPoint) {
      this.clearTieBreakChoice(match);
    }

    if (isAtTieBreakPoint && !lastGame?.extensions?.find((ext) => ext.name === 'squashTieBreakChoice')) return true;

    return false;
  }

  async selectTieBreakOption(numberOfPointsInGame: number): Promise<'suddenDeath' | 'winBy2' | undefined> {
    const showCancel = false;
    const options: IocSelectorOption[] = [];

    if (numberOfPointsInGame === 9) {
      options.push({ label: 'Set to 9 (sudden death)', value: 'suddenDeath' }, { label: 'Set to 10 (win by 2)', value: 'winBy2' });
    } else if (numberOfPointsInGame === 15) {
      options.push({ label: 'Set to 15 (sudden death)', value: 'suddenDeath' }, { label: 'Set to 17 (win by 2)', value: 'winBy2' });
    } else {
      return undefined;
    }

    return this.iocSelectorService.openPopover('Select tie-break option', options, showCancel);
  }

  getTieBreakChoice(match: Match): 'suddenDeath' | 'winBy2' | undefined {
    const lastGame = this.bhs.getLastGame(match);
    return lastGame?.extensions?.find((e) => e.name === 'squashTieBreakChoice')?.value as
      | 'suddenDeath'
      | 'winBy2'
      | undefined;
  }

  storeTieBreakChoice(match: Match, choice: 'suddenDeath' | 'winBy2'): void {
    const lastGame = this.bhs.getLastGame(match);
    if (!lastGame) return;
    if (!lastGame.extensions) lastGame.extensions = [];

    const idx = lastGame.extensions.findIndex((e) => e.name === 'squashTieBreakChoice');
    if (idx >= 0) lastGame.extensions.splice(idx, 1);
    lastGame.extensions.push({ name: 'squashTieBreakChoice', value: choice });
  }

  isAddedGame(
    match: Match,
    scoreFormat: ScoreFormat,
    nextToLastPointNumberOfPointsWonForWinner: number,
    nextToLastPointNumberOfPointsWonForLoser: number,
    winningSide: number
  ): boolean {
    const numberOfPointsInGame = scoreFormat.numberOfPointsInGame || 11;
    const currentWinnerScore = nextToLastPointNumberOfPointsWonForWinner + 1;
    const currentLoserScore = nextToLastPointNumberOfPointsWonForLoser;

    const gameType = this.getGameType(match);

    if (gameType === SquashGameType.ENGLISH_HIHO) {
      const choice = this.getTieBreakChoice(match);

      if (numberOfPointsInGame === 9) {
        if (choice === 'suddenDeath') return currentWinnerScore >= 9;
        if (choice === 'winBy2') return currentWinnerScore >= 10 && currentWinnerScore >= currentLoserScore + 2;
        return currentWinnerScore >= 9 && currentWinnerScore >= currentLoserScore + 2;
      }

      if (numberOfPointsInGame === 15) {
        if (choice === 'suddenDeath') return currentWinnerScore >= 15;
        if (choice === 'winBy2') return currentWinnerScore >= 17 && currentWinnerScore >= currentLoserScore + 2;
        return currentWinnerScore >= 15 && currentWinnerScore >= currentLoserScore + 2;
      }
    }

    return currentWinnerScore >= numberOfPointsInGame && currentWinnerScore >= currentLoserScore + 2;
  }

  public getGameType(match: Match): SquashGameType {
    const fmt = match.matchFormat || '';
    if (fmt.includes('ENGLISH') || fmt.includes('HIHO')) return SquashGameType.ENGLISH_HIHO;
    return SquashGameType.RALLY;
  }

  getSetType(match: Match): SetType {
    return SetType.RALLY;
  }

  isLastSet(match: Match, scoreFormat: ScoreFormat): boolean {
    return match.sets.length === scoreFormat.numberOfSets;
  }

  /* --------------------------- Private helpers --------------------------- */

  private buildPlayersMap(match: Match): PlayersMap {
    const map: PlayersMap = {};
    match.sides.forEach((side, i) => {
      map[i + 1] = side.players.map((player, j) => ({
        playerName: this.bhs.getPlayersNames(match, side.sideNumber, 'full', j + 1),
        sideNumber: i + 1,
        playerNumber: j + 1,
        player: player.participant?._id || null,
      }));
    });
    return map;
  }

  private buildServerOptions(players: PlayersMap, serverNumber?: number, sideNumber?: number): IocSelectorOption[] {
    return (Object.values(players) as any[])
      .flatMap((sidePlayers: any[], sideIndex) =>
        sidePlayers
          .filter((p: any) => !sideNumber || p.sideNumber === sideNumber)
          .map((p: any) => ({
            label: p.playerName,
            value: {
              ...p,
              serverNumber,
              returnerSideNumber: sideIndex === 0 ? 2 : 1,
              returningSide: CourtSide.DEUCE,
            },
          }))
      );
  }

  private async selectReturner(match: Match, returnerSideNumber = 1, showCancelButton = false): Promise<number | undefined> {
    const opts = match.sides[returnerSideNumber - 1].players.map((_, i) => ({
      label: this.bhs.getPlayersNames(match, returnerSideNumber, 'full', i + 1) ?? '',
      value: i + 1,
    }));

    return this.iocSelectorService.openPopover('Select returner', opts, showCancelButton);
  }

  private assignPlayerPositions(players: PlayersMap, selectedServer: Server): void {
    [1, 2].forEach((side) => {
      players[side].forEach((player) => {
        const isServerOrReturner =
          selectedServer.sideNumber === side
            ? player.playerNumber === selectedServer.playerNumber
            : player.playerNumber === selectedServer.returnerPlayerNumber;

        (this as any)[`side${side}PlayerPosition`][isServerOrReturner ? 'deuce' : 'ad'] = player;
      });
    });
  }

  private changeServerForSingles(match: Match, pointWinnerSideNumber: number, isNewSet?: boolean) {
    const nextToLastPoint = this.bhs.getNextToLastPoint(match);
    if (nextToLastPoint) {
      return { server: this.getRallySetServerOnChange(nextToLastPoint, match.sides, match.matchType, match, isNewSet), suggestServerChange: false };
    }

    const sideNumber = pointWinnerSideNumber;
    const playerNumber = 1;

    const server: Server = {
      sideNumber,
      playerNumber,
      player: match.sides[sideNumber - 1].players[0]?.participant?._id,
      returnerSideNumber: sideNumber === 1 ? 2 : 1,
      returnerPlayerNumber: 1,
      returningSide: CourtSide.DEUCE,
      returnerPlayerId: match.sides[(sideNumber === 1 ? 2 : 1) - 1]?.players?.[0]?.participant?._id ?? null,
    };

    return { server, suggestServerChange: false };
  }

  private changeServerForDoubles(match: Match, pointWinnerSideNumber: number, isNewSet?: boolean) {
    const nextToLastPoint = this.bhs.getNextToLastPoint(match);
    let suggestServerChange = false;

    // Beginning of a new set detection: if last set had one game and that game has one point
    const lastSet = this.bhs.getLastSet(match);
    const lastGame = this.bhs.getLastGame(match);
    if (
      match.sets.length > 1 &&
      lastSet?.games && lastSet.games.length === 1 &&
      lastGame?.points && lastGame.points.length === 1
    ) {
      const previousSet = this.bhs.getLastCompletedSet(match);
      const previousSetWinner = previousSet?.winningSide;
      if (previousSetWinner && previousSetWinner === pointWinnerSideNumber) {
        suggestServerChange = true;
      }
    }

    if (nextToLastPoint) {
      return { server: this.getRallySetServerOnChange(nextToLastPoint, match.sides, match.matchType, match, isNewSet), suggestServerChange };
    }

    // Default: winner serves (A1/B1 default to playerNumber=1)
    const sideNumber = pointWinnerSideNumber;
    const playerNumber = 1;

    const server: Server = {
      sideNumber,
      playerNumber,
      player: match.sides[sideNumber - 1].players[playerNumber - 1]?.participant?._id || '',
      returnerSideNumber: sideNumber === 1 ? 2 : 1,
      returnerPlayerNumber: 1,
      returningSide: CourtSide.DEUCE,
      returnerPlayerId: match.sides[(sideNumber === 1 ? 2 : 1) - 1]?.players?.[0]?.participant?._id ?? null,
    };

    return { server, suggestServerChange };
  }

  public getRallySetServerOnChange(
    nextToLastPoint: Point,
    sides: Side[],
    matchType: string,
    match: Match,
    isNewSet?: boolean
  ): Server {
    const cycle = [
      { s: 1, p: 1 },
      { s: 2, p: 1 },
      { s: 1, p: 2 },
      { s: 2, p: 2 },
    ];

    const prevServer = nextToLastPoint?.server;
    let serverSideNumber = nextToLastPoint.winningSide || 1;
    let serverPlayerNumber = 1;

    // Determine base server using prevServer + rally rules
    if (prevServer) {
      const serverWon = prevServer.sideNumber === nextToLastPoint.winningSide;

      if (matchType === 'D') {
        if (serverWon) {
          serverSideNumber = prevServer.sideNumber || 1;
          serverPlayerNumber = prevServer.playerNumber || 1;
        } else {
          const prevIndex = cycle.findIndex((c) => c.s === prevServer.sideNumber && c.p === (prevServer.playerNumber ?? 1));
          const nextIndex = prevIndex >= 0 ? (prevIndex + 1) % cycle.length : 0;
          serverSideNumber = cycle[nextIndex].s;
          serverPlayerNumber = cycle[nextIndex].p;
        }
      } else {
        // Singles
        if (serverWon) {
          serverSideNumber = prevServer.sideNumber || 1;
          serverPlayerNumber = 1;
        } else {
          serverSideNumber = nextToLastPoint.winningSide || 1;
          serverPlayerNumber = 1;
        }
      }
    } else {
      // No prev server, winner becomes server
      serverSideNumber = nextToLastPoint.winningSide || 1;
      serverPlayerNumber = matchType === 'S' ? 1 : 1;
    }

    const returnerSideNumber = serverSideNumber === 1 ? 2 : 1;

    const serverWon = prevServer && prevServer.sideNumber === nextToLastPoint.winningSide;

    // returningSide determination
    let returningSide: CourtSide = CourtSide.DEUCE;
    const lastGame = this.bhs.getLastGame(match);
    const nextToLastGame = this.bhs.getNextToLastGame(match);
    const isNewGame = Boolean(lastGame && lastGame.gameNumber > 1 && lastGame.points.length === 1);

    if (matchType === 'S') returningSide = CourtSide.DEUCE;
    else if (isNewSet || isNewGame) returningSide = CourtSide.DEUCE;
    else if (serverWon && prevServer) returningSide = prevServer.returningSide || CourtSide.DEUCE;
    else returningSide = CourtSide.DEUCE;

    const serverPlayerId = sides[serverSideNumber - 1]?.players?.[serverPlayerNumber - 1]?.participant?._id;

    // Determine returner
    let returnerPlayerNumber: number;
    let returnerPlayerId: string | undefined;

    if (matchType === 'S') {
      returnerPlayerNumber = 1;
      returnerPlayerId = sides[returnerSideNumber - 1]?.players?.[0]?.participant?._id ?? undefined;
    } else {
      if (serverWon && prevServer && prevServer.returnerPlayerNumber) {
        returnerPlayerNumber = prevServer.returnerPlayerNumber;
        returnerPlayerId = prevServer.returnerPlayerId || undefined;
      } else {
        const serverIndex = cycle.findIndex((c) => c.s === serverSideNumber && c.p === serverPlayerNumber);
        if (serverIndex >= 0) {
          const r = cycle[(serverIndex + 1) % cycle.length];
          if (r.s === returnerSideNumber) {
            returnerPlayerNumber = r.p;
            returnerPlayerId = sides[returnerSideNumber - 1]?.players?.[returnerPlayerNumber - 1]?.participant?._id ?? undefined;
          } else {
            returnerPlayerNumber = 1;
            returnerPlayerId = sides[returnerSideNumber - 1]?.players?.[0]?.participant?._id ?? undefined;
          }
        } else {
          returnerPlayerNumber = 1;
          returnerPlayerId = sides[returnerSideNumber - 1]?.players?.[0]?.participant?._id ?? undefined;
        }
      }
    }

    return {
      sideNumber: serverSideNumber,
      playerNumber: serverPlayerNumber,
      returnerSideNumber,
      returnerPlayerNumber,
      returningSide,
      player: serverPlayerId,
      returnerPlayerId,
    } as Server;
  }
}
